<template>
  <div class="card-resumen text-primary mt-20">
    <div class="p-20 p-m-15 d-flex align-items-center pointer" @click="showModal = true">
      <div
        class="plazas-aviso mr-20 flex-shrink-0"
        :class="showWarningAviso ? 'bg-rojo-100 text-rojo-500' : 'plazas-aviso-info text-primary'">
        <CustomIcon :icon="showWarningAviso ? avisoIcon : infoIcon" size="24px" size-mobile="14px" />
      </div>

      <div class="flex-grow-1 text-start min-w-0">
        <i18n-t
          v-if="suplementoPagado"
          :keypath="'cardReservaPlazasLibres.msjPlazaLibresPagadas'"
          tag="div"
          class="p small-m text-primary mb-none">
          <template #plazasLibres>
            <span class="semibold">{{ plazasLibres }}</span>
          </template>
        </i18n-t>

        <template v-else>
          <i18n-t :keypath="'cardReservaPlazasLibres.descripcion'" tag="div" class="p small-m text-primary mb-none">
            <template #plazasLibres>
              <span class="semibold">{{
                $t('cardReservaPlazasLibres.plazasLibresDestacado', { count: plazasLibres })
              }}</span>
            </template>
            <template #pagoPendiente>
              <span class="semibold">{{ formatPrice(pagoPendiente) }}{{ agenciaStore.getCurrency }}</span>
            </template>
          </i18n-t>

          <i18n-t
            v-if="pagoPendiente > 0"
            :keypath="'cardReservaPlazasLibres.cancelacion'"
            tag="p"
            class="small sub-m text-rojo-300 mb-none mt-10">
            <template #destacado>
              <span class="semibold">{{
                $t('cardReservaPlazasLibres.cancelacionDestacado', { fecha: formatDate(fechaCancelacion) })
              }}</span>
            </template>
          </i18n-t>
        </template>
      </div>

      <CustomIcon :icon="derechaIcon" size="16px" class="flex-shrink-0 mx-1 text-primary mx-md-3" />
    </div>

    <Transition name="modal">
      <div v-if="showModal" class="plazas-modal-mask" @click="showModal = false">
        <div class="plazas-modal-container p-20" @click.stop>
          <div class="d-flex justify-content-between align-items-start mb-20">
            <h6 class="semibold text-primary mb-none text-start pe-10">
              {{
                plazasLibres === 1
                  ? $t('cardReservaPlazasLibres.modal.tituloSingular', { count: plazasLibres })
                  : $t('cardReservaPlazasLibres.modal.titulo', { count: plazasLibres })
              }}
            </h6>
            <button
              type="button"
              class="btn-close flex-shrink-0"
              :aria-label="$t('botones.cerrar')"
              @click="showModal = false" />
          </div>

          <p class="p text-primary text-start mb-20">{{ $t('cardReservaPlazasLibres.modal.subtitulo') }}</p>

          <CardUrlUnirse v-if="viaje" :viaje="viaje" :codigo-grupo="codigoGrupo" :is-b2b="isB2b" />

          <template v-if="!suplementoPagado">
            <div class="plazas-separador my-30">
              <span class="plazas-separador-text p text-primary">{{
                $t('cardReservaPlazasLibres.modal.separador')
              }}</span>
            </div>

            <MyGocuButton secondary class="w-100" :label="abonarPagoLabel" @click="handlerAbonarPago" />
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { formatDate } from '@/helpers/dateUtiles';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { formatPrice } from '@/helpers/numberUtils';
  import avisoIcon from '@/assets/icons/raw/aviso.svg';
  import infoIcon from '@/assets/icons/raw/info.svg';
  import derechaIcon from '@/assets/icons/raw/derecha.svg';
  import CustomIcon from '@/components/CustomIcon.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import CardUrlUnirse from '@/components/card/unirse/CardUrlUnirse.vue';

  const props = defineProps({
    plazasLibres: {
      type: Number,
      required: true,
    },
    pagoPendiente: {
      type: Number,
      required: true,
    },
    fechaCancelacion: {
      type: String,
      default: '',
    },
    suplementoPagado: {
      type: Boolean,
      default: false,
    },
    viaje: {
      type: Object,
      default: null,
    },
    codigoGrupo: {
      type: String,
      default: '',
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
  });

  const emits = defineEmits(['abonarPago']);

  const { t } = useI18n();
  const agenciaStore = agenciaStoreModule();
  const showModal = ref(false);

  const showWarningAviso = computed(() => !props.suplementoPagado && props.pagoPendiente > 0);

  const abonarPagoLabel = computed(() =>
    t('cardReservaPlazasLibres.button.abonarPago', {
      pagoPendiente: `${formatPrice(props.pagoPendiente)}${agenciaStore.getCurrency}`,
    })
  );

  function handlerAbonarPago() {
    showModal.value = false;
    emits('abonarPago');
  }
</script>

<style scoped lang="scss">
  .card-resumen {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .plazas-aviso {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--bs-border-radius-lg);
    width: 44px;
    height: 44px;

    @media screen and (max-width: 768px) {
      width: 26px;
      height: 26px;
    }
  }

  .plazas-aviso-info {
    background-color: rgba(var(--bs-primary-rgb), 0.1);
  }

  .plazas-modal-mask {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(var(--bs-primary-rgb), 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s ease;
  }

  .plazas-modal-container {
    max-width: 600px;
    width: 90vw;
    background-color: white;
    box-shadow: 0 2px 8px rgba(var(--bs-primary-rgb), 0.3);
    border-radius: var(--bs-border-radius-xl);

    @media screen and (max-width: 768px) {
      max-width: 95vw;
    }
  }

  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
  }

  .modal-enter-from .plazas-modal-container,
  .modal-leave-to .plazas-modal-container {
    transform: scale(1.05);
  }

  .plazas-separador {
    text-align: center;
    position: relative;
  }

  .plazas-separador-text {
    background-color: white;
    padding: 0 10px;
    position: relative;
    z-index: 1;
  }

  .plazas-separador::after {
    content: '';
    background: rgba(var(--bs-primary-rgb), 0.2);
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    z-index: 0;
  }
</style>
